import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import {
  getPractice, updatePractice, updateProfile, getProfile,
  completeOnboarding, initiateStripeOnboarding, getStripeConnectStatus,
} from '../services/settingsService';
import {
  listAppointmentTypes, createAppointmentType,
} from '../services/appointmentTypeService';
import { lookupNPI } from '../services/nppesService';
import Spinner from '../components/common/Spinner';

// ── Schemas ──────────────────────────────────────────────────────────────────

const practiceSchema = z.object({
  name: z.string().min(1, 'Practice name is required').max(255),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z.union([z.string().email('Invalid email'), z.literal('')]).optional(),
  addressLine1: z.string().max(255).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(2).optional().or(z.literal('')),
  zip: z.string().max(10).optional().or(z.literal('')),
  npi: z.string().max(10).optional().or(z.literal('')),
  ein: z.string().max(20).optional().or(z.literal('')),
  taxonomyCode: z.string().max(20).optional().or(z.literal('')),
});

const providerSchema = z.object({
  firstName: z.string().min(1, 'Required').max(100),
  lastName: z.string().min(1, 'Required').max(100),
  npi: z.string().max(10).optional().or(z.literal('')),
  taxonomyCode: z.string().max(20).optional().or(z.literal('')),
  credentials: z.string().max(50).optional().or(z.literal('')),
  licenseNumber: z.string().max(50).optional().or(z.literal('')),
  licenseState: z.string().max(2).optional().or(z.literal('')),
});

const apptTypeSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  durationMinutes: z.coerce.number().int().min(1).max(480),
  cptCode: z.string().max(10).optional().or(z.literal('')),
  price: z.union([z.coerce.number().min(0), z.literal('')]).optional(),
});

// ── NPI lookup widget ─────────────────────────────────────────────────────────

function NpiLookup({ onResult, expectedType }) {
  const [npiInput, setNpiInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async () => {
    const val = npiInput.trim();
    if (!/^\d{10}$/.test(val)) {
      setError('NPI must be exactly 10 digits');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const result = await lookupNPI(val);
      if (!result) {
        setError('NPI not found in registry');
        return;
      }
      if (expectedType === 'individual' && !result.isIndividual) {
        setError('That NPI belongs to an organization (Type 2). Enter your individual (Type 1) NPI here.');
        return;
      }
      if (expectedType === 'organization' && result.isIndividual) {
        // Solo practitioners legitimately use a Type 1 NPI for their practice — allow it through.
      }
      onResult(result);
      setNpiInput('');
    } catch {
      setError('NPPES lookup failed — check your connection and try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-4">
      <p className="text-sm font-medium text-primary-800 dark:text-primary-300 mb-2">
        Look up from NPPES registry
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          className="input flex-1 font-mono"
          placeholder="10-digit NPI number"
          value={npiInput}
          onChange={(e) => setNpiInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onKeyDown={(e) => e.key === 'Enter' && lookup()}
          maxLength={10}
        />
        <button
          type="button"
          onClick={lookup}
          disabled={isLoading}
          className="btn btn-primary flex items-center gap-1.5 disabled:opacity-60"
        >
          {isLoading
            ? <Spinner className="h-4 w-4" />
            : <MagnifyingGlassIcon className="h-4 w-4" />}
          Look up
        </button>
      </div>
      {error && <p className="mt-1.5 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
      <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
        We&apos;ll pre-fill name, address, and taxonomy code from the public NPPES registry.
      </p>
    </div>
  );
}

// ── Step indicators ───────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Practice' },
  { id: 2, label: 'Provider' },
  { id: 3, label: 'Appt. Types' },
  { id: 4, label: 'Payments' },
];

function StepIndicator({ current }) {
  return (
    <nav className="flex items-center justify-center gap-0 mb-10">
      {STEPS.map((step, i) => {
        const done = step.id < current;
        const active = step.id === current;
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                done ? 'bg-primary-600 text-white'
                : active ? 'border-2 border-primary-600 text-primary-600 bg-white dark:bg-neutral-900'
                : 'border-2 border-neutral-300 dark:border-neutral-600 text-neutral-400 bg-white dark:bg-neutral-900'
              }`}>
                {done ? <CheckCircleIcon className="h-4 w-4" /> : step.id}
              </div>
              <span className={`mt-1 text-xs font-medium ${
                active ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-400 dark:text-neutral-500'
              }`}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px w-12 sm:w-20 mx-1 mb-4 transition-colors ${
                done ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
              }`} />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ── Field helper ──────────────────────────────────────────────────────────────

const Field = ({ label, error, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

// ── COMMON_CPT_CODES ──────────────────────────────────────────────────────────

const COMMON_CPT_CODES = [
  { code: '', label: 'None' },
  { code: '90791', label: '90791 — Psychiatric diagnostic evaluation' },
  { code: '90792', label: '90792 — Psychiatric diagnostic evaluation w/ medical services' },
  { code: '90832', label: '90832 — Psychotherapy, 30 min' },
  { code: '90834', label: '90834 — Psychotherapy, 45 min' },
  { code: '90837', label: '90837 — Psychotherapy, 60 min' },
  { code: '90847', label: '90847 — Family psychotherapy with patient, 50 min' },
  { code: '90846', label: '90846 — Family psychotherapy without patient, 50 min' },
  { code: '90853', label: '90853 — Group psychotherapy' },
  { code: '90839', label: '90839 — Psychotherapy for crisis, 60 min' },
];

// ── Step 1: Practice Info ─────────────────────────────────────────────────────

function StepPractice({ onNext }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['settings', 'practice'], queryFn: getPractice });
  const { data: profileData } = useQuery({ queryKey: ['settings', 'profile'], queryFn: getProfile });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(practiceSchema),
    values: data?.practice ? {
      name: data.practice.name ?? '',
      phone: data.practice.phone ?? '',
      email: data.practice.email || profileData?.user?.email || '',
      addressLine1: data.practice.addressLine1 ?? '',
      city: data.practice.city ?? '',
      state: data.practice.state ?? '',
      zip: data.practice.zip ?? '',
      npi: data.practice.npi ?? '',
      ein: data.practice.ein ?? '',
      taxonomyCode: data.practice.taxonomyCode ?? '',
    } : undefined,
  });

  const mutation = useMutation({
    mutationFn: updatePractice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      onNext();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
  });

  const handleNppesResult = (result) => {
    const practiceName = result.organizationName
      || (result.firstName || result.lastName ? `${result.firstName} ${result.lastName}`.trim() : '');
    if (practiceName) setValue('name', practiceName);
    if (result.address1) setValue('addressLine1', result.address1);
    if (result.city) setValue('city', result.city);
    if (result.state) setValue('state', result.state);
    if (result.zip) setValue('zip', result.zip);
    if (result.phone) setValue('phone', result.phone);
    if (result.npi) setValue('npi', result.npi);
    if (result.taxonomyCode) setValue('taxonomyCode', result.taxonomyCode);
    toast.success('Pre-filled from NPPES registry');
  };

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
      <NpiLookup onResult={handleNppesResult} expectedType="organization" />

      <div className="space-y-4">
        <Field label="Practice name *" error={errors.name}>
          <input type="text" className="input" {...register('name')} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone" error={errors.phone}>
            <input type="tel" className="input" {...register('phone')} />
          </Field>
          <Field label="Email" error={errors.email}>
            <input type="email" className="input" {...register('email')} />
          </Field>
        </div>
        <Field label="Address" error={errors.addressLine1}>
          <input type="text" className="input" placeholder="123 Main St" {...register('addressLine1')} />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="City" error={errors.city}>
            <input type="text" className="input" {...register('city')} />
          </Field>
          <Field label="State" error={errors.state}>
            <input type="text" className="input" maxLength={2} {...register('state')} />
          </Field>
          <Field label="ZIP" error={errors.zip}>
            <input type="text" className="input" {...register('zip')} />
          </Field>
        </div>

        <hr className="border-neutral-200 dark:border-neutral-700" />
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Billing identifiers — required for superbills and CMS-1500 forms
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Group NPI (Type 2)" error={errors.npi}
            hint="10-digit National Provider Identifier for your organization">
            <input type="text" className="input font-mono" maxLength={10} placeholder="1234567890" {...register('npi')} />
          </Field>
          <Field label="EIN" error={errors.ein} hint="Employer Identification Number (12-3456789)">
            <input type="text" className="input font-mono" maxLength={20} placeholder="12-3456789" {...register('ein')} />
          </Field>
        </div>
        <Field label="Taxonomy code" error={errors.taxonomyCode}
          hint="NUCC taxonomy code identifying your practice type (e.g. 101YM0800X)">
          <input type="text" className="input font-mono" maxLength={20} placeholder="e.g. 101YM0800X" {...register('taxonomyCode')} />
        </Field>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex items-center gap-2 disabled:opacity-60">
          {mutation.isPending ? 'Saving…' : <>Next <ArrowRightIcon className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
}

// ── Step 2: Provider Info ─────────────────────────────────────────────────────

function StepProvider({ onNext, onBack }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['settings', 'profile'], queryFn: getProfile });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(providerSchema),
    values: data ? {
      firstName: data.user?.firstName ?? '',
      lastName: data.user?.lastName ?? '',
      npi: data.practitioner?.npi ?? '',
      taxonomyCode: data.practitioner?.taxonomyCode ?? '',
      credentials: data.practitioner?.credentials ?? '',
      licenseNumber: data.practitioner?.licenseNumber ?? '',
      licenseState: data.practitioner?.licenseState ?? '',
    } : undefined,
  });

  const mutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
      onNext();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
  });

  const handleNppesResult = (result) => {
    if (result.firstName) setValue('firstName', result.firstName);
    if (result.lastName) setValue('lastName', result.lastName);
    if (result.credential) setValue('credentials', result.credential);
    if (result.npi) setValue('npi', result.npi);
    if (result.taxonomyCode) setValue('taxonomyCode', result.taxonomyCode);
    toast.success('Pre-filled from NPPES registry');
  };

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
      <NpiLookup onResult={handleNppesResult} expectedType="individual" />

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name *" error={errors.firstName}>
            <input type="text" className="input" {...register('firstName')} />
          </Field>
          <Field label="Last name *" error={errors.lastName}>
            <input type="text" className="input" {...register('lastName')} />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Individual NPI (Type 1)" error={errors.npi}
            hint="Your personal 10-digit NPI — appears in Box 24J on claims">
            <input type="text" className="input font-mono" maxLength={10} placeholder="1234567890" {...register('npi')} />
          </Field>
          <Field label="Taxonomy code" error={errors.taxonomyCode}
            hint="NUCC taxonomy identifying your license type">
            <input type="text" className="input font-mono" maxLength={20} placeholder="e.g. 101YM0800X" {...register('taxonomyCode')} />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Credentials" error={errors.credentials} hint="e.g. LMHC, LCSW, PhD">
            <input type="text" className="input" maxLength={50} placeholder="LMHC" {...register('credentials')} />
          </Field>
          <Field label="License number" error={errors.licenseNumber}>
            <input type="text" className="input" maxLength={50} {...register('licenseNumber')} />
          </Field>
          <Field label="License state" error={errors.licenseState}>
            <input type="text" className="input" maxLength={2} placeholder="NY" {...register('licenseState')} />
          </Field>
        </div>
      </div>

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="btn btn-secondary flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" /> Back
        </button>
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex items-center gap-2 disabled:opacity-60">
          {mutation.isPending ? 'Saving…' : <>Next <ArrowRightIcon className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
}

// ── Step 3: Appointment Types ─────────────────────────────────────────────────

function StepAppointmentTypes({ onNext, onBack }) {
  const queryClient = useQueryClient();
  const { data: types = [], isLoading } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: listAppointmentTypes,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(apptTypeSchema),
    defaultValues: { name: '', durationMinutes: 50, cptCode: '', price: '' },
  });

  const createMutation = useMutation({
    mutationFn: createAppointmentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentTypes'] });
      reset({ name: '', durationMinutes: 50, cptCode: '', price: '' });
      toast.success('Appointment type added');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create'),
  });

  return (
    <div className="space-y-6">
      {/* Existing types */}
      {isLoading ? (
        <div className="flex justify-center py-4"><Spinner /></div>
      ) : types.length > 0 ? (
        <div>
          <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Added so far</h3>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-700">
            {types.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className="h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: t.color || '#6b7280' }}
                />
                <span className="flex-1 text-sm font-medium text-neutral-900 dark:text-neutral-100">{t.name}</span>
                <span className="text-xs text-neutral-500">{t.durationMinutes} min</span>
                {t.cptCode && (
                  <span className="text-xs font-mono text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded">
                    {t.cptCode}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Add form */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {types.length === 0 ? 'Add your first appointment type' : 'Add another'}
        </h3>
        <form onSubmit={handleSubmit((d) => createMutation.mutate({
          ...d,
          price: d.price === '' ? null : d.price,
          cptCode: d.cptCode === '' ? null : d.cptCode,
        }))} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name *" error={errors.name}>
              <input type="text" className="input" placeholder="e.g. Initial Intake" {...register('name')} />
            </Field>
            <Field label="Duration (min) *" error={errors.durationMinutes}>
              <select className="input" {...register('durationMinutes')}>
                {[30, 45, 50, 60, 75, 90, 120].map((d) => (
                  <option key={d} value={d}>{d} min</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="CPT code" error={errors.cptCode}>
              <select className="input" {...register('cptCode')}>
                {COMMON_CPT_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Default rate ($)" error={errors.price}>
              <input type="number" step="0.01" min="0" className="input" placeholder="0.00" {...register('price')} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={createMutation.isPending} className="btn btn-secondary disabled:opacity-60">
              {createMutation.isPending ? 'Adding…' : '+ Add type'}
            </button>
          </div>
        </form>
      </div>

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="btn btn-secondary flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={types.length === 0}
          className="btn btn-primary flex items-center gap-2 disabled:opacity-60"
          title={types.length === 0 ? 'Add at least one appointment type to continue' : undefined}
        >
          Next <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
      {types.length === 0 && (
        <p className="text-xs text-center text-amber-600 dark:text-amber-400">
          Add at least one appointment type to continue.
        </p>
      )}
    </div>
  );
}

// ── Step 4: Payments ──────────────────────────────────────────────────────────

function StepPayments({ onFinish, onBack }) {
  const { data: stripeStatus } = useQuery({
    queryKey: ['stripe', 'status'],
    queryFn: getStripeConnectStatus,
  });

  const onboardMutation = useMutation({
    mutationFn: initiateStripeOnboarding,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Stripe onboarding failed'),
  });

  const isConnected = stripeStatus?.connected && stripeStatus?.onboardingComplete;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 text-center space-y-3">
        {isConnected ? (
          <>
            <CheckCircleSolid className="h-10 w-10 text-green-500 mx-auto" />
            <p className="font-medium text-neutral-900 dark:text-neutral-100">Stripe connected</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Clients can pay invoices online and payouts go directly to your bank account.
            </p>
          </>
        ) : (
          <>
            <p className="font-medium text-neutral-900 dark:text-neutral-100">Connect Stripe to accept payments</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-sm mx-auto">
              Accept credit cards for invoices and copays. Payouts go directly to your bank. You can skip this and connect later in Settings.
            </p>
            <button
              type="button"
              onClick={() => onboardMutation.mutate()}
              disabled={onboardMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {onboardMutation.isPending ? 'Redirecting…' : 'Connect Stripe'}
            </button>
          </>
        )}
      </div>

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="btn btn-secondary flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" /> Back
        </button>
        <button type="button" onClick={onFinish} className="btn btn-primary flex items-center gap-2">
          {isConnected ? 'Finish setup' : 'Skip for now'} <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);

  const completeMutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      navigate('/');
    },
    onError: () => navigate('/'),
  });

  const STEP_TITLES = {
    1: { title: 'Tell us about your practice', sub: 'We\'ll use this on superbills and CMS-1500 forms.' },
    2: { title: 'Your clinical credentials', sub: 'Your individual NPI appears on every claim you generate.' },
    3: { title: 'Appointment types', sub: 'Define the sessions you offer. You can always add more later.' },
    4: { title: 'Accept online payments', sub: 'Optional — you can skip this and connect Stripe later.' },
  };

  const { title, sub } = STEP_TITLES[step];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col items-center justify-start pt-12 px-4 pb-20">
      <div className="w-full max-w-xl">
        {/* Logo / brand */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Welcome to Sage</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Let&apos;s set up your practice in a few steps.</p>
        </div>

        <StepIndicator current={step} />

        <div className="card">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{sub}</p>
          </div>

          {step === 1 && (
            <StepPractice onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <StepProvider onNext={() => setStep(3)} onBack={() => setStep(1)} />
          )}
          {step === 3 && (
            <StepAppointmentTypes onNext={() => setStep(4)} onBack={() => setStep(2)} />
          )}
          {step === 4 && (
            <StepPayments
              onFinish={() => completeMutation.mutate()}
              onBack={() => setStep(3)}
            />
          )}
        </div>

        <p className="text-center text-xs text-neutral-400 dark:text-neutral-500 mt-6">
          You can update all of these settings later in{' '}
          <button
            type="button"
            onClick={() => completeMutation.mutate()}
            className="underline hover:text-neutral-600 dark:hover:text-neutral-300"
          >
            Settings
          </button>.
        </p>
      </div>
    </div>
  );
}
